<template>
  <div class="sf-auth-page">
    <div class="sf-auth-glow-a" />
    <div class="sf-auth-glow-b" />

    <div class="sf-auth-container">
      <div
        v-if="empresaLogo"
        class="sf-auth-logo-row"
      >
        <b-img
          :src="empresaLogo"
          alt="logo"
        />
      </div>

      <div class="sf-auth-card">
        <b-overlay
          :show="loadingOverlay"
          rounded="sm"
        >
          <div class="sf-auth-head">
            <div class="sf-auth-eyebrow">
              {{ appName }}
            </div>
            <h1 class="sf-auth-title">
              {{ $t('registro.titulo') }}
            </h1>
          </div>

          <b-alert
            v-if="sponsor === 'null'"
            show
            variant="danger"
            class="mb-3"
          >
            <div class="alert-body">
              <feather-icon
                class="mr-25"
                icon="UserIcon"
              />
              <span class="ml-25">{{ $t('registro.subtitulo') }}</span>
            </div>
          </b-alert>

          <b-alert
            v-if="sponsor && sponsor !== 'null'"
            show
            variant="warning"
            class="mb-3"
          >
            <h4 class="alert-heading">
              {{ $t('registro.patrocionador') }}
            </h4>
            <div class="alert-body">
              <feather-icon
                class="mr-25"
                icon="UserIcon"
              />
              <span class="ml-25">{{ sponsor.nome }}</span>
            </div>
          </b-alert>

          <!-- form -->
          <validation-observer ref="registerForm">
            <b-form
              v-if="sponsor && sponsor !== 'null'"
              class="sf-auth-form"
              @submit.prevent="register"
            >
              <!-- name -->
              <validation-provider
                #default="{ errors }"
                name="Name"
                rules="required"
              >
                <div class="sf-field">
                  <label for="name">{{ $t('registro.nome') }}</label>
                  <div class="sf-input-wrap">
                    <feather-icon
                      icon="UserIcon"
                      class="sf-input-icon"
                    />
                    <b-form-input
                      id="name"
                      v-model="name"
                      :state="errors.length > 0 ? false:null"
                      name="register-name"
                      placeholder="John Carter"
                    />
                  </div>
                  <small
                    v-if="errors[0]"
                    class="sf-error"
                  >{{ errors[0] }}</small>
                </div>
              </validation-provider>

              <!-- telephone -->
              <validation-provider
                #default="{ errors, touched }"
                name="Telephone"
                rules="required"
              >
                <div class="sf-field">
                  <label for="telephone">{{ $t('registro.telefone') }}</label>
                  <div class="sf-input-wrap sf-tel-wrap">
                    <feather-icon
                      icon="PhoneIcon"
                      class="sf-input-icon"
                    />
                    <vue-tel-input
                      id="telephone"
                      v-model="telephone"
                      mode="international"
                      :state="errors.length > 0 ? false : null"
                      name="register-telephone"
                      :valid-characters-only="true"
                      :auto-format="true"
                      :input-options="{
                        styleClasses: 'form-control',
                      }"
                      :dropdown-options="{
                        showDialCodeInList: true,
                        showDialCodeInSelection: false,
                        showFlags: true,
                        showSearchBox: false
                      }"
                      :preferred-countries="['BR','BO','PE','EC','CL']"
                    >
                      <template v-slot:arrow-icon>
                        <span /> </template>
                    </vue-tel-input>
                  </div>
                  <small
                    v-if="errors[0] && touched"
                    class="sf-error"
                  >{{ errors[0] }}</small>
                </div>
              </validation-provider>

              <!-- email -->
              <validation-provider
                #default="{ errors }"
                name="Email"
                rules="required|email"
              >
                <div class="sf-field">
                  <label for="email">{{ $t('registro.email') }}</label>
                  <div class="sf-input-wrap">
                    <feather-icon
                      icon="MailIcon"
                      class="sf-input-icon"
                    />
                    <b-form-input
                      id="email"
                      v-model="emailUser"
                      :state="errors.length > 0 ? false:null"
                      name="register-email"
                      placeholder="voce@empresa.com"
                    />
                  </div>
                  <small
                    v-if="errors[0]"
                    class="sf-error"
                  >{{ errors[0] }}</small>
                </div>
              </validation-provider>

              <!-- password -->
              <validation-provider
                #default="{ errors }"
                name="Password"
                rules="required"
              >
                <div class="sf-field">
                  <label for="password">{{ $t('registro.senha') }}</label>
                  <div class="sf-input-wrap has-toggle">
                    <feather-icon
                      icon="LockIcon"
                      class="sf-input-icon"
                    />
                    <b-form-input
                      id="password"
                      v-model="password"
                      :type="passwordFieldType"
                      :state="errors.length > 0 ? false:null"
                      name="register-password"
                      placeholder="••••••••"
                    />
                    <feather-icon
                      class="sf-toggle-btn"
                      :icon="passwordToggleIcon"
                      @click="togglePasswordVisibility"
                    />
                  </div>
                  <small
                    v-if="errors[0]"
                    class="sf-error"
                  >{{ errors[0] }}</small>
                </div>
              </validation-provider>

              <!-- submit button -->
              <button
                type="submit"
                class="sf-submit-btn"
              >
                {{ $t('registro.registrar') }}
                <feather-icon icon="ArrowRightIcon" />
              </button>
            </b-form>
          </validation-observer>
        </b-overlay>

        <div class="sf-auth-footnote">
          <span>{{ $t('registro.temconta') }} </span>
          <b-link :to="{name:'auth-login'}">
            {{ $t('registro.entraraqui') }}
          </b-link>
        </div>

        <div
          v-if="sponsor"
          class="sf-recaptcha-note"
        >
          This site is protected by reCAPTCHA and the Google
          <a href="https://policies.google.com/privacy">Privacy Policy</a>
          and
          <a href="https://policies.google.com/terms">Terms of Service</a>
          apply
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BLink, BForm, BFormInput, BImg, BAlert, BOverlay,
} from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'

import Vue from 'vue'
import axios from '@axios'
import { VueReCaptcha } from 'vue-recaptcha-v3'
import { required, email } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import { getHomeRouteForLoggedInUser } from '@/auth/utils'
import useAppConfig from '@core/app-config/useAppConfig'
import { $themeConfig } from '@themeConfig'
import { VueTelInput } from 'vue-tel-input'
import 'vue-tel-input/dist/vue-tel-input.css'

Vue.use(VueReCaptcha, {
  siteKey: process.env.VUE_APP_GOOGLE_CAPTCHA_SITE_KEY,
  loaderOptions: {
    autoHideBadge: true,
  },
})

export default {
  components: {
    BLink,
    BForm,
    BFormInput,
    BImg,
    BAlert,
    BOverlay,
    // validations
    ValidationProvider,
    ValidationObserver,
    VueTelInput,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      emailUser: '',
      telephone: '',
      name: '',
      password: '',
      sponsor: null,
      loadingOverlay: false,
      // validation rules
      required,
      email,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },

  setup() {
    const { empresa, empresaLogo } = useAppConfig()
    const { appName } = $themeConfig.app

    return {
      empresa,
      empresaLogo,
      appName,
    }
  },

  mounted() {
    if (this.empresa.modoTela === 'semi-dark') {
      document.body.classList.add('dark-layout')
    }
    this.fetchSponsorDetails()
  },

  beforeDestroy() {
    document.body.classList.remove('dark-layout')
  },

  methods: {

    async fetchSponsorDetails() {
      this.loadingOverlay = true
      try {
        const response = await axios.get('/publico/usuario/indicacao-codigo', { params: { codigo: this.$route.params.sponsor } })
        this.sponsor = response.data
      } catch (error) {
        this.sponsor = 'null'
      } finally {
        this.loadingOverlay = false
      }
    },

    recaptcha() {
      this.loadingOverlay = true
      const self = this
      this.$recaptchaLoaded().then(() => {
        this.$recaptcha('register').then(token => {
          this.token = token
          useJwt.register({
            email: this.emailUser,
            telefone: this.telephone.replace(/[^+\d]/g, ''),
            nome: this.name,
            senha: this.password,
            indicacaoCodigo: this.sponsor.codigo,
            empresa: this.empresa.id,
            token: this.token,
          }).then(response => {
            useJwt.setToken(response.data.token)
            localStorage.setItem('userData', JSON.stringify(response.data))

            self.$ability.update(response.data.ability)
            this.$router.replace(getHomeRouteForLoggedInUser(response.data.perfil))
          }).catch(error => {
            let errorMessage = 'An error has occurred, please try again'

            if (error.data) {
              const errorMessages = error.data.map(err => `${err.campo}: ${err.erro}`)

              errorMessage = errorMessages.join('\n')
            }

            this.$swal({
              title: 'Erro!',
              text: errorMessage,
              icon: 'error',
              customClass: {
                confirmButton: 'btn btn-primary',
              },
              buttonsStyling: false,
            })
          }).finally(() => {
            this.loadingOverlay = false
          })
        })
      })
    },

    register() {
      this.$refs.registerForm.validate().then(success => {
        if (success) {
          this.recaptcha()
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-sweetalert.scss';

/* ============================================================
   Autenticação — padrão Soulf (dark premium).
   design_handoff_soulf_redesign/reference/Login.dc.html
   ============================================================ */
.sf-auth-page {
  min-height: 100vh;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  position: relative;
  overflow: hidden;
  font-family: 'Geist', sans-serif;
}

.sf-auth-glow-a {
  position: absolute;
  width: 520px; height: 520px;
  border-radius: 50%;
  background: rgba(37, 99, 235, 0.08);
  filter: blur(90px);
  top: -160px; right: -120px;
  pointer-events: none;
}
.sf-auth-glow-b {
  position: absolute;
  width: 420px; height: 420px;
  border-radius: 50%;
  background: rgba(37, 99, 235, 0.06);
  filter: blur(90px);
  bottom: -140px; left: -100px;
  pointer-events: none;
}

.sf-auth-container {
  width: 100%;
  max-width: 420px;
  position: relative;
  z-index: 1;
}

.sf-auth-logo-row {
  display: flex;
  justify-content: center;
  margin-bottom: 28px;

  img { max-height: 40px; width: auto; }
}

.sf-auth-card {
  background: #0F0F11;
  border: 1px solid rgba(255, 255, 255, 0.10);
  border-radius: 20px;
  padding: 36px 34px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.5), transparent);
  }
}

.sf-auth-head { margin-bottom: 28px; }
.sf-auth-eyebrow {
  font-family: 'Geist Mono', 'SFMono-Regular', Menlo, monospace;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 10px;
}
.sf-auth-title {
  font-size: 24px;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.02em;
  margin: 0 0 6px;
}
.sf-auth-subtitle {
  font-size: 13.5px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.5;
  margin: 0;
}

.sf-auth-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sf-field + .sf-field { margin-top: 0; }
.sf-field label {
  display: block;
  font-size: 12.5px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.70);
  margin-bottom: 7px;
}
.sf-field-headrow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;

  label { margin-bottom: 0; }
}
.sf-forgot-link {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  &:hover { color: #fff; text-decoration: none; }
}

.sf-input-wrap {
  position: relative;

  .sf-input-icon {
    position: absolute;
    left: 13px; top: 50%;
    transform: translateY(-50%);
    color: rgba(255, 255, 255, 0.35);
    pointer-events: none;
    width: 16px; height: 16px;
  }

  ::v-deep .form-control,
  ::v-deep input.form-control {
    width: 100%;
    background: rgba(255, 255, 255, 0.04) !important;
    border: 1px solid rgba(255, 255, 255, 0.08) !important;
    border-radius: 9px !important;
    padding: 11px 13px 11px 38px !important;
    height: auto !important;
    color: #fff !important;
    font: 400 13.5px 'Geist', sans-serif !important;
    box-shadow: none !important;
    transition: border-color .15s ease, box-shadow .15s ease;

    &::placeholder { color: rgba(255, 255, 255, 0.35) !important; }
    &:focus {
      border-color: rgba(59, 130, 246, 0.6) !important;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15) !important;
    }
    &.is-invalid { border-color: #ef4444 !important; }
  }

  &.has-toggle ::v-deep .form-control { padding-right: 40px !important; }

  .sf-toggle-btn {
    position: absolute;
    right: 6px; top: 50%;
    transform: translateY(-50%);
    width: 30px; height: 30px;
    display: flex; align-items: center; justify-content: center;
    color: rgba(255, 255, 255, 0.40);
    cursor: pointer;
    border-radius: 7px;
    transition: color .15s ease;
    &:hover { color: #fff; }
  }
}

.sf-error {
  color: #f87171;
  font-size: 12px;
  margin-top: 6px;
  display: block;
}

.sf-submit-btn {
  margin-top: 6px;
  width: 100%;
  border: none;
  background: #3b82f6;
  color: #fff;
  font: 600 13.5px 'Geist', sans-serif;
  padding: 12px 0;
  border-radius: 9999px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 0 32px rgba(59, 130, 246, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.15), inset 0 -1px 0 rgba(0, 0, 0, 0.25);
  transition: transform .15s ease, box-shadow .15s ease, opacity .15s ease;

  .feather { width: 16px; height: 16px; }

  &:hover:not(:disabled) { transform: translateY(-2px); }
  &:disabled { opacity: 0.5; box-shadow: none; cursor: not-allowed; transform: none; }
}

.sf-recaptcha-note {
  margin-top: 26px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
  line-height: 1.5;
  text-align: center;

  a {
    color: rgba(255, 255, 255, 0.55);
    &:hover { color: #fff; text-decoration: underline; }
  }
}

.sf-back-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 22px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);

  .feather { width: 15px; height: 15px; }
  &:hover { color: #fff; text-decoration: none; }
}

.sf-error-fade-enter-active, .sf-error-fade-leave-active { transition: all .2s ease; }
.sf-error-fade-enter, .sf-error-fade-leave-to { opacity: 0; transform: translateY(-6px); }

@media (max-width: 480px) {
  .sf-auth-card { padding: 28px 22px; border-radius: 16px; }
}
</style>

<style lang="scss" scoped>
.sf-auth-footnote {
  text-align: center;
  margin-top: 20px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);

  a, ::v-deep a {
    color: #3b82f6 !important;
    font-weight: 600;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
}

/* vue-tel-input: mesma casca escura dos outros inputs, com o ícone à esquerda */
.sf-tel-wrap {
  ::v-deep .vue-tel-input {
    width: 100%;
    background: transparent;
    border: none;
  }
  ::v-deep .vti__input.form-control {
    width: 100%;
    background: rgba(255, 255, 255, 0.04) !important;
    border: 1px solid rgba(255, 255, 255, 0.08) !important;
    border-radius: 9px !important;
    padding: 11px 13px 11px 38px !important;
    height: auto !important;
    color: #fff !important;
    font: 400 13.5px 'Geist', sans-serif !important;
    box-shadow: none !important;

    &:focus {
      border-color: rgba(59, 130, 246, 0.6) !important;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15) !important;
    }
  }
  ::v-deep .vti__dropdown {
    background: transparent;
    &:hover { background: rgba(255, 255, 255, 0.04); }
  }
  ::v-deep .vti__dropdown-list {
    background: #1C1C1E;
    border: 1px solid rgba(255, 255, 255, 0.10);
    color: #fff;
  }
  ::v-deep .vti__dropdown-item.highlighted { background: rgba(255, 255, 255, 0.06); }
}
</style>
